import React, { useState, useMemo, useRef } from 'react'
import { Box, Chip, IconButton, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { TimePicker } from '@mui/x-date-pickers'
import dayjs, { Dayjs } from 'dayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { useApplicationSuiteContext } from '../../contexts/ApplicationSuiteProvider'
import { ScheduleWindow } from '../../common/backtestSchedule'

dayjs.extend(customParseFormat)

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MAX_RANGES_PER_DAY = 3

type ScheduleSelectProps = {
  timezone: string
  windows: ScheduleWindow[]
  onWindowsChange: (windows: ScheduleWindow[]) => void
  disabled?: boolean
  backtestMode?: boolean
}

function ScheduleSelect({
  timezone,
  windows,
  onWindowsChange,
  disabled,
  backtestMode,
}: ScheduleSelectProps) {
  const { canEditAlertTimeframes } = useApplicationSuiteContext()
  const isDisabled = backtestMode ? (disabled ?? false) : disabled || !canEditAlertTimeframes

  const [editingIdx, setEditingIdx] = useState<number | null>(null)
  const [draggingIdx, setDraggingIdx] = useState<number | null>(null)
  const lastUsedTimes = useRef<{ startTime: string; endTime: string }>({
    startTime: '09:30',
    endTime: '16:00',
  })

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }))

  const windowsByDay = useMemo(() => {
    const map: Record<number, { idx: number; win: ScheduleWindow }[]> = {}
    for (let d = 0; d < 7; d++) map[d] = []
    windows.forEach((win, idx) => {
      if (win.dayOfWeek >= 0 && win.dayOfWeek <= 6) {
        map[win.dayOfWeek].push({ idx, win })
      }
    })
    for (let d = 0; d < 7; d++) {
      map[d].sort((a, b) => a.win.startTime.localeCompare(b.win.startTime))
    }
    return map
  }, [windows])

  const handleAddRange = (dayOfWeek: number) => {
    const { startTime, endTime } = lastUsedTimes.current
    const next = [...windows, { dayOfWeek, startTime, endTime }]
    onWindowsChange(next)
    setEditingIdx(next.length - 1)
  }

  const handleRemove = (idx: number) => {
    onWindowsChange(windows.filter((_, i) => i !== idx))
    setEditingIdx(null)
  }

  const handleSave = (idx: number, updated: ScheduleWindow) => {
    lastUsedTimes.current = { startTime: updated.startTime, endTime: updated.endTime }
    const next = [...windows]
    next[idx] = updated
    onWindowsChange(next)
    setEditingIdx(null)
  }

  const handleDragStart = ({ active }: DragStartEvent) => {
    const idx = Number((active.id as string).replace('window-', ''))
    setDraggingIdx(idx)
    setEditingIdx(null)
  }

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    setDraggingIdx(null)
    if (!over) return

    const draggedIdx = Number((active.id as string).replace('window-', ''))
    const targetDay = Number((over.id as string).replace('day-', ''))
    if (isNaN(draggedIdx) || isNaN(targetDay)) return

    const draggedWindow = windows[draggedIdx]
    if (draggedWindow.dayOfWeek === targetDay) return
    if (windowsByDay[targetDay].length >= MAX_RANGES_PER_DAY) return

    const next = [...windows]
    next[draggedIdx] = { ...draggedWindow, dayOfWeek: targetDay }
    onWindowsChange(next)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <Box id="scheduleSelect">
          <Box display="flex" flexDirection="column" gap={0.75}>
            {DAY_LABELS.map((dayLabel, day) => {
              const dayWindows = windowsByDay[day]
              const hasRanges = dayWindows.length > 0
              const atDayCap = dayWindows.length >= MAX_RANGES_PER_DAY

              return (
                <Box key={day} display="flex" alignItems="center" gap={1} minHeight="32px">
                  <Typography
                    variant="body2"
                    sx={{
                      width: '36px',
                      flexShrink: 0,
                      fontWeight: hasRanges ? 600 : 400,
                      color: hasRanges ? 'text.primary' : 'text.disabled',
                    }}
                  >
                    {dayLabel}
                  </Typography>

                  <DroppableDay day={day} atCap={atDayCap}>
                    {dayWindows.map(({ idx, win }) =>
                      editingIdx === idx ? (
                        <ScheduleWindowEditor
                          key={idx}
                          win={win}
                          disabled={isDisabled}
                          onSave={(updated) => handleSave(idx, updated)}
                        />
                      ) : (
                        <DraggableChip
                          key={idx}
                          idx={idx}
                          win={win}
                          disabled={isDisabled}
                          isGhost={draggingIdx === idx}
                          onEdit={() => setEditingIdx(idx)}
                          onRemove={() => handleRemove(idx)}
                        />
                      )
                    )}

                    {!isDisabled &&
                      !atDayCap &&
                      !dayWindows.some(({ idx }) => idx === editingIdx) && (
                        <IconButton
                          size="small"
                          onClick={() => handleAddRange(day)}
                          sx={{ width: '24px', height: '24px' }}
                        >
                          <AddIcon sx={{ fontSize: '16px' }} />
                        </IconButton>
                      )}
                  </DroppableDay>
                </Box>
              )
            })}
          </Box>
        </Box>

        <DragOverlay dropAnimation={null}>
          {draggingIdx !== null ? (
            <Box sx={{ filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.35))', cursor: 'grabbing' }}>
              <ScheduleWindowChip
                win={windows[draggingIdx]}
                disabled={false}
                onClick={() => {}}
                onRemove={() => {}}
              />
            </Box>
          ) : null}
        </DragOverlay>
      </DndContext>
    </LocalizationProvider>
  )
}

// ─── Droppable day row ───────────────────────────────────────────────────────

function DroppableDay({
  day,
  atCap,
  children,
}: {
  day: number
  atCap: boolean
  children: React.ReactNode
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `day-${day}`, disabled: atCap })

  return (
    <Box
      ref={setNodeRef}
      display="flex"
      flexWrap="wrap"
      gap={0.75}
      alignItems="center"
      flex={1}
      sx={{
        borderRadius: '6px',
        minHeight: '32px',
        transition: 'background-color 0.1s',
        backgroundColor: isOver && !atCap ? 'action.hover' : 'transparent',
      }}
    >
      {children}
    </Box>
  )
}

// ─── Draggable chip wrapper ──────────────────────────────────────────────────

function DraggableChip({
  idx,
  win,
  disabled,
  isGhost,
  onEdit,
  onRemove,
}: {
  idx: number
  win: ScheduleWindow
  disabled: boolean
  isGhost: boolean
  onEdit: () => void
  onRemove: () => void
}) {
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: `window-${idx}`,
    disabled,
  })

  return (
    <Box
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      sx={{
        opacity: isGhost ? 0.25 : 1,
        cursor: disabled ? 'default' : 'grab',
        touchAction: 'none',
      }}
    >
      <ScheduleWindowChip win={win} disabled={disabled} onClick={onEdit} onRemove={onRemove} />
    </Box>
  )
}

// ─── Read-only chip ──────────────────────────────────────────────────────────

function formatTime(hhmm: string): string {
  if (hhmm === '24:00') return 'End of Day'
  const d = dayjs(hhmm, 'HH:mm')
  return d.isValid() ? d.format('h:mm A') : hhmm
}

function windowLabel(win: ScheduleWindow): string {
  if (win.startTime === win.endTime || (win.startTime === '00:00' && win.endTime === '24:00'))
    return 'All day'
  return `${formatTime(win.startTime)} – ${formatTime(win.endTime)}`
}

function ScheduleWindowChip({
  win,
  disabled,
  onClick,
  onRemove,
}: {
  win: ScheduleWindow
  disabled: boolean
  onClick: () => void
  onRemove: () => void
}) {
  return (
    <Chip
      label={windowLabel(win)}
      size="small"
      onClick={disabled ? undefined : onClick}
      onDelete={disabled ? undefined : onRemove}
      sx={{
        cursor: disabled ? 'default' : 'pointer',
        height: '28px',
        '& .MuiChip-label': { fontSize: '0.75rem', px: 1 },
      }}
    />
  )
}

// ─── Inline editor ───────────────────────────────────────────────────────────

function ScheduleWindowEditor({
  win,
  disabled,
  onSave,
}: {
  win: ScheduleWindow
  disabled: boolean
  onSave: (updated: ScheduleWindow) => void
}) {
  const [draft, setDraft] = useState<ScheduleWindow>(win)
  const [pickerOpen, setPickerOpen] = useState(false)

  const displayEnd = draft.endTime === '24:00' ? '00:00' : draft.endTime
  const startValue: Dayjs | null = dayjs(draft.startTime, 'HH:mm')
  const endValue: Dayjs | null = dayjs(displayEnd, 'HH:mm')

  const handleStartChange = (v: Dayjs | null) => {
    if (!v?.isValid()) return
    setDraft((d) => ({ ...d, startTime: v.format('HH:mm') }))
  }

  const handleEndChange = (v: Dayjs | null) => {
    if (!v?.isValid()) return
    const formatted = v.format('HH:mm')
    setDraft((d) => ({ ...d, endTime: formatted === '00:00' ? '24:00' : formatted }))
  }

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (pickerOpen) return
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
      onSave(draft)
    }
  }

  const pickerProps = { onOpen: () => setPickerOpen(true), onClose: () => setPickerOpen(false) }
  const pickerSx = {
    width: '130px',
    '& .MuiInputBase-root': { height: '32px' },
    '& .MuiInputAdornment-root': { ml: 0 },
  }

  return (
    <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap" onBlur={handleBlur}>
      <TimePicker
        label="Start"
        value={startValue}
        onChange={handleStartChange}
        disabled={disabled}
        ampm={true}
        ampmInClock={true}
        views={['hours', 'minutes']}
        {...pickerProps}
        slotProps={{ textField: { autoFocus: true } }}
        sx={pickerSx}
      />
      <Typography variant="body2" sx={{ flexShrink: 0 }}>
        –
      </Typography>
      <TimePicker
        label="End"
        value={endValue}
        onChange={handleEndChange}
        onAccept={handleEndChange}
        disabled={disabled}
        ampm={true}
        ampmInClock={true}
        views={['hours', 'minutes']}
        {...pickerProps}
        sx={pickerSx}
      />
    </Box>
  )
}

export default ScheduleSelect
